/**
 * Types for the Flight Pairing Status tool.
 *
 * The backend evaluates every flight's crew coverage into a cache collection (`flightpairingstatuscache`);
 * the client only reads, filters and renders those documents. These types mirror the backend's
 * `IFlightPairingStatusCache` (backend-utils `database/flight/flightPairingStatusCache.ts`) as they arrive over
 * JSON (ObjectIds and dates serialised to strings).
 *
 * TODO: move the shared shapes into oc-interfaces once the backend change has shipped.
 */

export type FlightPairingStatusCode =
  | "NO_PAIRING"
  | "MISSING_CREW"
  | "EXTRA_CREW"
  | "EXTRA_POSITION"
  | "MISSING_AC_INFO"
  | "OK";

export interface IFlightPairingStatusDescriptor {
  code: FlightPairingStatusCode;
  priority: number;
  /** Theme key: no_pairing | missing_crew | extra_crew | extra_pos | missing_ac_info | ok */
  className: string;
  label: string;
  message: string;
}

export interface IFlightPairingStatusPositionCount {
  positionCode: string;
  count: number;
}

export interface IFlightPairingStatusActualCrew {
  /** Crew member id, null for an unfilled pairing position slot. */
  crew: string | null;
  pos: string;
  posId: string | null;
  pairing: string;
  prgCode: string;
  overridden: boolean;
}

export interface IFlightPairingStatusCrewSummary {
  required: number;
  assigned: number;
  missing: IFlightPairingStatusPositionCount[];
  extraCrew: IFlightPairingStatusPositionCount[];
  extraPositions: IFlightPairingStatusPositionCount[];
}

/** One cached flight as returned by `GET /flights/pairingStatusV2/{start}/{end}`. */
export interface IFlightPairingStatusRow {
  _id?: string;
  flightId: string;
  flightNumber: number;
  flightNumberSuffix?: string | null;
  origin: string;
  destination: string;
  /** ISO date string. */
  scheduledOut: string;
  actualOut?: string | null;
  /** UTC calendar day, YYYY-MM-DD. */
  dayKey: string;
  aircraft: { _id: string; registration: string; planning: boolean; subCharter: boolean } | null;
  flightType: { _id: string; title: string; color: string } | null;
  aircraftConfiguration: { cfgId: string; configTitle: string; revisionEffectiveDate?: string | null } | null;
  desiredCrew: IFlightPairingStatusPositionCount[];
  actualCrew: IFlightPairingStatusActualCrew[];
  pairings: { _id: string; shortId: string }[];
  pairingShortCodes: string[];
  status: IFlightPairingStatusDescriptor;
  crewSummary: IFlightPairingStatusCrewSummary;
  signature?: string;
  /** ISO date string. */
  computedAt: string;
  computeReason: string;
}

export interface IFlightPairingStatusMeta {
  /** Cached flights departing in the range. */
  count: number;
  /** Flights departing in the range according to the flights collection. */
  flightCount?: number;
  lastComputedAt: number | null;
  oldestComputedAt: number | null;
  range: { start: number; end: number };
  computedOnDemand: boolean;
  /**
   * The UTC days the server's scheduled refresh re-evaluates every interval, or null when it does not run in this
   * environment. Rows outside it are re-evaluated only when something about them changes. Absent from servers that
   * predate it.
   */
  scheduledWindow?: IEpochRange | null;
}

export interface IFlightPairingStatusPage {
  queryResult: IFlightPairingStatusRow[];
  lastId: string | null;
  meta?: IFlightPairingStatusMeta;
}

export interface IFlightPairingStatusRecomputeResponse {
  computedAt: number;
  computed: number;
  changed: number;
  removed: number;
  /** True when the server answered from a recompute that finished moments ago instead of running again. */
  skipped?: boolean;
}

/** Payload of the `FlightPairingStatusUpdate` websocket message. */
export interface IFlightPairingStatusWebsocketUpdate {
  computedAt: number;
  reason: string;
  flightIds?: string[];
  removedFlightIds?: string[];
  docs?: IFlightPairingStatusRow[];
  range?: { start: number; end: number };
  /**
   * Sent by every completed scheduled refresh, whether or not anything changed (with `reason: "scheduled"`):
   * every flight filed under the UTC days (`dayKey`) of this range was evaluated at `computedAt`. Whole UTC days
   * in epoch ms (start = 00:00:00.000Z of the first day, end = 23:59:59.999Z of the last).
   */
  verifiedRange?: { start: number; end: number };
}

/** Epoch millisecond range, inclusive. */
export interface IEpochRange {
  start: number;
  end: number;
}

/**
 * Status catalogue used for legends, filters and ordering. Mirrors the backend catalogue; the backend remains
 * the source of truth for a flight's status (`row.status`).
 */
export const FLIGHT_PAIRING_STATUS_CATALOGUE: Record<FlightPairingStatusCode, IFlightPairingStatusDescriptor> = {
  NO_PAIRING: {
    code: "NO_PAIRING",
    priority: 1,
    className: "no_pairing",
    label: "No Pairing",
    message: "No pairing found for this flight",
  },
  MISSING_CREW: {
    code: "MISSING_CREW",
    priority: 2,
    className: "missing_crew",
    label: "Missing Crew",
    message: "Required positions are not filled on the linked pairing(s)",
  },
  EXTRA_CREW: {
    code: "EXTRA_CREW",
    priority: 3,
    className: "extra_crew",
    label: "Extra Crew",
    message: "More crew than required in a required position on the linked pairing(s)",
  },
  EXTRA_POSITION: {
    code: "EXTRA_POSITION",
    priority: 4,
    className: "extra_pos",
    label: "Extra Position",
    message: "Crew assigned to a position the aircraft configuration does not require",
  },
  MISSING_AC_INFO: {
    code: "MISSING_AC_INFO",
    priority: 5,
    className: "missing_ac_info",
    label: "Missing AC Info",
    message: "No aircraft configuration revision with required crew counts applies to this flight",
  },
  OK: {
    code: "OK",
    priority: 7,
    className: "ok",
    label: "Ok",
    message: "Crew requirements met",
  },
};

/** Status codes ordered by severity (most severe first). */
export const FLIGHT_PAIRING_STATUS_ORDER: FlightPairingStatusCode[] = (
  Object.values(FLIGHT_PAIRING_STATUS_CATALOGUE)
)
  .sort((a, b) => a.priority - b.priority)
  .map((s) => s.code);

/** Statuses that need somebody's attention. */
export const PROBLEM_STATUS_CODES: FlightPairingStatusCode[] = FLIGHT_PAIRING_STATUS_ORDER.filter(
  (code) => code !== "OK"
);

/**
 * Websocket message type for cache updates. Must match `FLIGHT_PAIRING_STATUS_UPDATE_MESSAGE_TYPE` in
 * backend-utils (`helpers/websocket.ts`).
 *
 * TODO: replace with `WebsocketMessageType.FlightPairingStatusUpdate` (= 47) once added to oc-interfaces.
 */
export const FLIGHT_PAIRING_STATUS_UPDATE_MESSAGE_TYPE = 47;

/** Row key for the calendar view: all flights with the same number and routing share a row. */
export const routeKeyOf = (row: Pick<IFlightPairingStatusRow, "flightNumber" | "origin" | "destination">): string =>
  `${row.flightNumber}|${row.origin}|${row.destination}`;
